import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { SortOrders } from '@confluence/template-utils/entry-points/SortByDropdown.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useNewUserTemplates } from '@confluence/experiment-land-users-in-editor-with-revamped-bpqa/entry-points/useNewUserTemplates.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import SuggestedTemplatesQueryNode, {
	type useSuggestedTemplatesQuery$data,
	type useSuggestedTemplatesQuery$variables,
} from './__generated__/useSuggestedTemplatesQuery.graphql.ts';
import { BPQA_EXCLUDED_TEMPLATE_KEYS, TEMPLATE_LIMIT } from './bpqaConst.ts';

type Template = NonNullable<
	NonNullable<NonNullable<useSuggestedTemplatesQuery$data['allTemplates']>['nodes']>[number]
>;

type SuggestedTemplatesProps = {
	spaceKey: string;
};

export const useSuggestedTemplates = ({
	spaceKey,
}: SuggestedTemplatesProps):
	| {
			templates: never[];
			loading: boolean;
			error: ApolloErrorInterop | undefined;
			isNewUserTemplates?: undefined;
	  }
	| {
			templates: {
				name: string;
				author: string | null | undefined;
				blueprintModuleCompleteKey: string | null | undefined;
				categoryIds: ReadonlyArray<string | null | undefined>;
				contentBlueprintId: string | null | undefined;
				darkModeIconURL: string | null | undefined;
				description: string | null | undefined;
				hasGlobalBlueprintContent: boolean;
				hasWizard: boolean | null | undefined;
				iconURL: string | null | undefined;
				isFavourite: boolean | null | undefined;
				isNew: boolean | null | undefined;
				isPromoted: boolean | null | undefined;
				itemModuleCompleteKey: string | null | undefined;
				keywords: ReadonlyArray<string | null | undefined> | null | undefined;
				recommendationRank: number | null | undefined;
				styleClass: string | null | undefined;
				templateId: string | null | undefined;
				templateType: string | null | undefined;
			}[];
			loading: boolean;
			error: ApolloErrorInterop | undefined;
			isNewUserTemplates: boolean;
	  }
	| {
			error: ApolloErrorInterop | undefined;
			templates: {
				readonly author: string | null | undefined;
				readonly blueprintModuleCompleteKey: string | null | undefined;
				readonly categoryIds: ReadonlyArray<string | null | undefined>;
				readonly contentBlueprintId: string | null | undefined;
				readonly darkModeIconURL: string | null | undefined;
				readonly description: string | null | undefined;
				readonly hasGlobalBlueprintContent: boolean;
				readonly hasWizard: boolean | null | undefined;
				readonly iconURL: string | null | undefined;
				readonly isFavourite: boolean | null | undefined;
				readonly isNew: boolean | null | undefined;
				readonly isPromoted: boolean | null | undefined;
				readonly itemModuleCompleteKey: string | null | undefined;
				readonly keywords: ReadonlyArray<string | null | undefined> | null | undefined;
				readonly name: string | null | undefined;
				readonly recommendationRank: number | null | undefined;
				readonly styleClass: string | null | undefined;
				readonly templateId: string | null | undefined;
				readonly templateType: string | null | undefined;
			}[];
			loading: false;
			isNewUserTemplates?: undefined;
	  } => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		templates: newUserTemplates,
		error: newUserTemplatesError,
		loading: newUserTemplatesLoading,
	} = useNewUserTemplates();
	const { data, error, loading } = useQueryInterop<
		useSuggestedTemplatesQuery$data,
		useSuggestedTemplatesQuery$variables
	>(SuggestedTemplatesQueryNode, {
		variables: {
			spaceKey,
			sortingScheme: SortOrders.RELEVANCY_SORT,
			limit: TEMPLATE_LIMIT,
		},
		skip: !spaceKey,
	});
	if (error || newUserTemplatesError) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		if (isUnauthorizedError(newUserTemplatesError)) {
			markErrorAsHandled(newUserTemplatesError);
		}
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'error',
				actionSubject: 'useSuggestedTemplates',
				attributes: {
					version: '2',
					revampedBpqaError: Boolean(newUserTemplatesError),
				},
			},
		}).fire();
	}

	if (loading || newUserTemplatesLoading) {
		return {
			templates: [],
			loading: true,
			error: error || newUserTemplatesError,
		};
	}

	if (newUserTemplates) {
		return {
			templates: newUserTemplates,
			loading: false,
			error: error || newUserTemplatesError,
			isNewUserTemplates: true,
		};
	}

	const suggestedTemplates = (data?.allTemplates?.nodes as Template[]) || [];
	// we want to filter out the blog post template and legacy
	// editor template, so we get 5 templates and return the
	// first 3 excluding them if they exist
	const templates = suggestedTemplates
		?.filter(
			(template) =>
				template.itemModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.BLOG_POST &&
				template.blueprintModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.LEGACY_EDITOR,
		)
		?.slice(0, 3);
	return { error: error || newUserTemplatesError, templates, loading };
};
